import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";

import { ImageData, State } from "../../interfaces";
import {
  removeUploadedFlashCardImage,
  removeUploadedPlanImage,
} from "../../store/actions";
import { useDispatch } from "react-redux";

interface ImageUploaderProps {
  multiple?: boolean;
  removable?: boolean;
  removeAfterUpload?: boolean;
  required?: boolean;
  initialImages?: string[];
  onChangeImages: (images: ImageData[]) => void;
  uploadMediaState?: State<any>[];
  padding?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  multiple = true,
  removable = true,
  initialImages = [],
  onChangeImages,
  uploadMediaState,
  padding,
  removeAfterUpload = true,
}) => {
  //#region hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region internal state
  const [images, setImages] = useState<ImageData[]>([]);
  const [error, setError] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  //#endregion

  //#region effects

  useEffect(() => {
    if (images.length === 0 && initialImages.length > 0) {
      const loadedImages = initialImages.map((url, index) => ({
        id: `uploaded-${index}`,
        preview: url,
        localId: index + 1,
      }));
      setImages(loadedImages);
    }
  }, [initialImages]);

  useEffect(() => {
    onChangeImages(images);
  }, [images]);

  useEffect(() => {
    if (uploadMediaState) {
      const successfulUploads = uploadMediaState
        .filter((u) => u.success) // Filter images with success status
        .map((u) => u.localId); // Extract their localIds

      if (removeAfterUpload) {
        if (successfulUploads.length > 0) {
          // Remove from component state
          setImages((prevImages) =>
            prevImages.filter(
              (image) => !successfulUploads.includes(image.localId as number)
            )
          );

          // Dispatch action to remove from Redux store
          successfulUploads.forEach((localId) => {
            dispatch(removeUploadedFlashCardImage(localId as number));
          });
        }
      }
    }
  }, [uploadMediaState]);

  //#endregion

  //#region handlers
  const onDrop = (acceptedFiles: File[]) => {
    const newImages: ImageData[] = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    if (multiple) {
      setImages((prevImages) =>
        [...prevImages, ...newImages].map((ni, index: number) => ({
          ...ni,
          localId: index + 1,
        }))
      );
    } else {
      // Replace with new image
      setImages(
        newImages.map((ni, index: number) => ({ ...ni, localId: index + 1 }))
      );
    }
    setError(""); // Clear error on valid input
  };

  const validateFile = (file: File): any => {
    if (file.name.length > 200) {
      return {
        code: "name-too-large",
        message: `Name is larger than ${200} characters`,
      };
    }
    if (
      file &&
      !file.name.match(
        /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/
      )
    ) {
      return {
        code: "Invalid Image",
        message: t("selectValidImage"),
      };
    }
    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: multiple,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    validator: validateFile,
  });

  const removeImage = (index: number) => {
    if (!removable) return;
    const updatedImages = images.filter((_, i) => i !== index);
    dispatch(removeUploadedFlashCardImage(images[index].localId as number));
    dispatch(removeUploadedPlanImage(images[index].localId as number));
    setImages(updatedImages);
  };

  //#endregion

  return (
    <>
      <div>
        <div
          {...getRootProps()}
          style={{
            border: "2px dashed #cccccc",
            padding: padding || "100px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.3s ease-in-out",
            backgroundColor: isDragging ? "#f0f8ff" : "white",
            boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.2)" : "none",
          }}
        >
          <input {...getInputProps()} />
          <p style={{ marginTop: "1em" }}>
            {multiple
              ? t("dragAndDropMultipleImages")
              : t("dragAndDropSingleImage")}
          </p>
          <em className="text-danger">{t("imageValidationMessage")}</em>
        </div>
        {error && <p style={{ color: "red" }}>{error}</p>}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
          }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                maxWidth: "180px",
                marginTop: 10,
                marginRight: 10,
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <img
                  src={image.preview}
                  alt={`preview-${index}`}
                  style={{
                    width: "180px",
                    height: "180px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    borderWidth: 0.5,
                    borderColor: "#d3d3d3",
                    border: "solid",
                  }}
                />
                {uploadMediaState && (
                  <p
                    className="text-danger text-center my-1"
                    style={{ maxWidth: "180px" }}
                  >
                    {
                      uploadMediaState.find(
                        (u) =>
                          u.localId === image.localId &&
                          !u.loading &&
                          u.error &&
                          !u.success
                      )?.error
                    }
                  </p>
                )}
                {removable && images.length > 1 && (
                  <button
                    onClick={() => removeImage(index)}
                    style={{
                      position: "absolute",
                      textAlign: "center",
                      top: "5px",
                      right: "5px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 16,
                    }}
                  >
                    <span className="text-danger">&times;</span>
                  </button>
                )}
                {uploadMediaState &&
                  uploadMediaState.find(
                    (u) =>
                      u.localId === image.localId && (u.loading || u.success)
                  ) && (
                    <progress
                      style={{
                        height: 30,
                        position: "absolute",
                        bottom: 10,
                        left: 10,
                        right: 10,
                        width: 160,
                        paddingBottom: 30,
                      }}
                      value={
                        uploadMediaState.find(
                          (u) => u.localId === image.localId
                        )?.progress
                      }
                    ></progress>
                  )}
              </div>
              {image.file?.name && (
                <p>
                  {image.file?.name?.length > 50
                    ? image?.file?.name.substring(0, 49)
                    : image?.file?.name}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ImageUploader;
